/* <bedrock-tabs> — panel switcher for sections whose header carries a tab row.

   Panel 0 is live markup; every other panel ships inside <template data-index="N">
   and is cloned the first time its tab is clicked. That is the whole point of the
   element: a five-tab section costs one collection of markup and one collection of
   image requests on load, not five. Cloned panels stay in the DOM afterwards, so the
   second visit to a tab is instant.

   The tab buttons live in the section header, outside this element, so they are
   located through the closest section rather than through this.querySelectorAll.

   Custom elements inside <template> are inert until the clone is inserted into the
   document — <bedrock-slider> upgrades on append, with its children already parsed. */

const BEDROCK_TABS_FADE_MS = 500;
const BEDROCK_TABS_SHIFT = '2.5rem';

if (!customElements.get('bedrock-tabs')) {
  customElements.define(
    'bedrock-tabs',
    class BedrockTabs extends HTMLElement {
      connectedCallback() {
        this.tabs = Array.from(
          this.closest('.shopify-section, .bedrock-arrivals')?.querySelectorAll('[role="tab"]') || []
        );
        if (this.tabs.length < 2) return;

        this.currentIndex = 0;
        this.tabs.forEach((tab) => tab.addEventListener('click', this.onTabClick.bind(this)));

        // Theme editor: selecting a tab block in the sidebar should reveal its panel.
        if (window.Shopify?.designMode) {
          this.addEventListener('shopify:block:select', (event) => {
            this.select(Number(event.target.dataset.index));
          });
        }
      }

      onTabClick(event) {
        this.select(Number(event.currentTarget.dataset.index));
      }

      select(index) {
        if (Number.isNaN(index) || index === this.currentIndex) return;

        const panel = this.panelFor(index);
        if (!panel) return;

        this.tabs.forEach((tab) => {
          const active = Number(tab.dataset.index) === index;
          tab.classList.toggle('is-active', active);
          tab.setAttribute('aria-selected', active ? 'true' : 'false');
        });

        this.panelFor(this.currentIndex)?.setAttribute('hidden', '');
        panel.removeAttribute('hidden');
        this.currentIndex = index;

        this.animateIn(panel);

        // The slider measured itself while hidden (zero width) if this is a first
        // reveal — re-measure now that it has a layout box.
        panel.querySelector('bedrock-slider')?.initPages?.();
      }

      panelFor(index) {
        const existing = this.querySelector(`.bedrock-arrivals__panel[data-index="${index}"]`);
        if (existing) return existing;

        const template = this.querySelector(`template[data-index="${index}"]`);
        if (!template) return null;

        const panel = template.content.cloneNode(true).firstElementChild;
        panel.hidden = true;
        this.appendChild(panel);
        return panel;
      }

      animateIn(panel) {
        if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;

        panel.animate(
          [
            { opacity: 0, transform: `translateY(${BEDROCK_TABS_SHIFT})` },
            { opacity: 1, transform: 'translateY(0)' },
          ],
          { duration: BEDROCK_TABS_FADE_MS, easing: 'cubic-bezier(0, 0, 0.3, 1)' }
        );
      }
    }
  );
}
